// cSpell:ignore Afrekening Andere bedrag Betreft Correctie Eindafrekening forfait huurcontract Naam panden Pcts privatief privé provisie recognised Samenvatting Scenariolaan Stap Totaal totaalexcl Tuinonderhoud verbruik vertrek
/* eslint-disable no-nested-ternary */
// Client-side builder for the StatementSnapshot expected by the
// createStatementPdf lambda. Re-uses the wizard's engine so the PDF preview
// matches the numbers the user saw in Stap 4 exactly.

import {
  getLocale,
  getLocalizedText,
  toLocale,
} from "@rentiohq/shared-frontend/dist/utils/i18n/i18n.utils";
import {
  getSettlementTitle,
  TSettlementKind,
} from "@rentiohq/shared-frontend/dist/utils/settlement.utils";
import {
  compareUnitLabels,
  provisionSliceLines,
} from "../../../../buildingSettlement.utils";
import { withBilledTarget } from "../../../../chargeTarget.utils";
import {
  departureCorrectionLabel,
  isDepartureCorrectionKey,
  parseDepartureCorrectionKey,
} from "../../../../departureCorrection.utils";
import {
  allocate,
  daysInPeriod,
  departureCompanyAmountsOfCost,
  isPerOccupant,
  overlapDays,
  OwnerResult,
  prorateByOccupancy,
} from "../../../../engine";
import {
  daysWithin,
  fixedFeeDaysOfLease,
} from "../../../../fixedFeePeriods.utils";
import {
  existedSince,
  unitAddressLabel,
  unitBoxLabel,
} from "../../../../groupUnits.utils";
import { keyDisplayName } from "../../../../keyName.utils";
import { todayIsoDay } from "../../../../localDay.utils";
import {
  fromCents,
  roundCents,
  toCents,
  vatOfAmount,
} from "../../../../money.utils";
import { formatWeight } from "../../../../numberInput.utils";
import { ProvisionSlice } from "../../../../provisionHistory";
import {
  costKindOf,
  isFixedFeeKindKey,
  ISettlementKind,
  shownKinds,
  sliceOfFixedFeeKind,
  TFixedFeeKindKey,
} from "../../../../settlementKinds.utils";
import { settlementStoredName } from "../../../../settlementName.utils";
import {
  categorySpecOf,
  compareBreakdownKeys,
  ownerPartKeyName,
} from "../../../../settlementView.utils";
import {
  IPostalAddress,
  IStatementOffice,
  postalAddressLines,
  unitPostalLines,
} from "../../../../statementParties.utils";
import {
  ApportionmentKey,
  Category,
  CATEGORY_KEYS,
  CATEGORY_LABELS_NL,
  ContractRef,
  DistributionMethod,
  extraProvisionTitle,
  IAlreadySettledLine,
  IExtraProvision,
  Invoice,
  ISettledDeparture,
  PerContractResult,
  resolveDistributionMethod,
  Settlement,
  settlementAmount,
  vatPercent,
} from "../../../../types";
import { AssignableProperty } from "./types";

// Mirror of the serverless module's `StatementSnapshot` type. Duplicated here
// to avoid dragging a serverless import into the dashboard bundle.
// Who a statement is from or to: a name and its postal address, one line
// each ("Scenariolaan 1 bus 2", "9000 Gent"); no lines when none is known.
export interface StatementParty {
  name: string;
  lines: string[];
}
export interface StatementLineDetail {
  date: string;
  description: string;
  supplier?: string;
  periodFrom: string;
  periodTo: string;
  splitLabel?: string;
  temporalLabel: string;
  invoiceExcl: number;
  recognizedExcl: number;
  vatRate: number;
  vatValue: number;
  ownerFixedPct?: number;
  ownerFixedExcl?: number;
  ownerVacancyExcl?: number;
  // Owner mode: the cost's differences with departure settlements of the
  // owner's units, each printed with its tenant (F85). Absent when none.
  ownerDepartureDiffs?: { tenantName: string; amount: number }[];
}
export interface StatementLineGroup {
  categoryKey: string;
  // i18n key of the category, so the PDF can print it in the recipient's
  // language; categoryKey stays as the fallback label.
  categoryI18nKey?: string;
  // What the broker specified for an "Other" cost ("Tuinonderhoud"), printed
  // after the category name in their own wording.
  categorySpecs?: string[];
  isPrivate?: boolean;
  subtotalExcl: number;
  subtotalVat: number;
  details: StatementLineDetail[];
  // Name of the apportionment key used to split this category's group costs
  // across the units. Undefined for unit-scope settlements (no key involved).
  apportionmentKeyName?: string;
  // Owner mode, consumption or metering-company category: the key that split
  // the owners' fixed part over the units (see ownerPartKeyName).
  ownerPartKeyName?: string;
  // A building settlement with a unit's own costs on this statement: whether
  // the category holds the building's common costs or one unit's own costs,
  // and that unit's full address ("Scenariolaan 1 bus 2, 9000 Gent"; "bus 2"
  // when its street is not known). The PDF then prints a sub-table with a
  // subtotal per section. Absent otherwise.
  section?: "building" | "unit";
  unitLabel?: string;
}
// One slice of the lease's own provision, printed on a line of its own.
export interface StatementProvisionLine {
  slice: ProvisionSlice;
  amount: number;
  months?: number;
}
// One provision against a kind's costs: a slice of the lease's provision
// ("common" / "private", named by the PDF), one of the lease's extra cost
// lines ("line"), or a provision added by hand ("added"), the last two under
// their title.
export interface StatementKindProvisionLine {
  type: "common" | "private" | "line" | "added";
  title?: string;
  amount: number;
  months?: number;
}
// One kind of the tenant's balance (settlementKinds.utils): its costs, the
// provisions against them and what is left, incl. VAT (> 0 to pay).
export interface StatementKind {
  // "common", "private", or "extra:<line>" for an extra cost line of the
  // lease that is a kind of its own.
  key: string;
  // An extra kind's name, frozen in the recipient's language.
  title?: string;
  costExcl: number;
  costVat: number;
  provisionLines: StatementKindProvisionLine[];
  balance: number;
}
export interface StatementVatBucket {
  rate: number;
  excl: number;
  vat: number;
}
export interface StatementSnapshot {
  mode: "tenant" | "owner";
  // Recipient's language (nl_BE / fr_FR / en_UK); the PDF renders in it.
  locale?: string;
  // The settlement's name, printed as the subject: the name the broker gave
  // it, as typed (Lewis, 2026-10-16); without one its kind and period in the
  // recipient's language (getSettlementTitle): "Afrekening 2026", or for a
  // departure "Eindafrekening bij vertrek — huurcontract tot 30/06/2026".
  // The suggested name never holds a tenant's name (Q2). Absent on older
  // statements, which print the period.
  title?: string;
  kind?: TSettlementKind;
  // The statement's number, "AFR-2026-0012": rentio-api-next assigns it when
  // it renders a confirmed settlement's statement (numbered per office and
  // year of confirmation), and marks any other one as a preview (isPreview).
  // The dashboard leaves it empty.
  reference: string;
  isPreview?: boolean;
  docDate: string;
  // Never set (Lewis, 2026-10-19): a one-off payment invitation is due at
  // once, so a due date on the statement would contradict it.
  dueDate?: string;
  buildingLabel: string;
  periodFrom: string;
  periodTo: string;
  // The letter's sender: the office of the settled property, never its
  // owner (Lewis, 2026-10-19). Absent for a property without an office.
  sender?: StatementParty;
  // The letter's addressee with their postal address: the lease's tenants
  // (at their own address, else at the leased unit), or the owner.
  recipient?: StatementParty;
  // The office's logo (PNG or JPEG) heading the statement: rentio-api-next
  // sets its current one when it renders the statement; the dashboard never.
  senderLogoUrl?: string;
  // Older statements: the addressee and "Rentio" by name only.
  beneficiary?: StatementParty;
  manager?: StatementParty;
  tenantName?: string;
  // Everyone on the lease: the tenants (tenantName joins them) and the
  // parents/guardians who are not tenants themselves.
  tenantNames?: string[];
  parentNames?: string[];
  contractStart?: string;
  contractEnd?: string;
  categoryGroups: StatementLineGroup[];
  grossExcl: number;
  grossVat: number;
  // Tenant mode: all provisions that come off the total, the ones the broker
  // added by hand (extraProvisions) included.
  provisionsExpected?: number;
  // Tenant mode: the provisions added by hand, each printed under its own
  // title below the lease's. Absent when there are none.
  extraProvisions?: { title: string; amount: number }[];
  // Tenant mode, building settlement: the lease's own provision per slice,
  // printed instead of the one "expected provisions" line. They add up to
  // provisionsExpected minus the extra provisions, to the cent (the PDF does
  // not check). Absent otherwise.
  provisionLines?: StatementProvisionLine[];
  // Tenant mode: the balance per kind (Lewis, 2026-10-16), printed instead of
  // the provision lines: a block per kind, then the one total. Only with two
  // kinds or more, adding up to the statement's own totals to the cent.
  // Absent otherwise, and on older statements.
  kinds?: StatementKind[];
  // Owner mode: the shares of this owner's units that departure settlements
  // already settled, never charged again (see ISettledDeparture). Absent when
  // there are none.
  alreadySettled?: IStatementAlreadySettled[];
  totalExcl: number;
  totalVat: number;
  balance: number;
  vatBuckets: StatementVatBucket[];
}

// A share a departure settlement already settled, as the owner's statement
// prints it.
export interface IStatementAlreadySettled {
  tenantName: string;
  unitLabel: string;
  until: string;
  categoryKey: string;
  categoryI18nKey?: string;
  // A unit's own cost: the PDF adds "(privatief)" to the category, as it does
  // for the statement's own lines.
  isPrivate?: boolean;
  amount: number;
}

interface BuildParams {
  mode: "tenant" | "owner";
  settlement: Settlement;
  // The settlement's name while confirming (the wizard's "Naam"); without
  // it the settlement's stored name (settlementStoredName).
  name?: string;
  contract: ContractRef | null;
  contracts: ContractRef[];
  costs: Invoice[]; // selected + eligible + not already settled
  keys: ApportionmentKey[];
  selectedKeyId: Partial<Record<Category, string>>;
  // The distribution choice per category (method + its inputs). Comes from the
  // wizard draft while confirming, and from the frozen snapshot afterwards —
  // without it the statement would have to assume "key" and would disagree with
  // the Samenvatting for verbruik-/bedrag-based categories.
  distribution?: {
    distributionMethod?: Record<string, DistributionMethod>;
    consumption?: Record<string, Record<string, number>>;
    buildingTotal?: Record<string, number>;
    excessMode?: Record<string, "key" | "landlord">;
    companyAmounts?: Record<string, Record<string, number>>;
  };
  assignable: AssignableProperty[];
  // The group's units the settlement divided by (archived ones included, see
  // groupUnits.utils). Absent: the group's units in `assignable`. A departure
  // settlement is a unit's, yet splits its group's costs over these too.
  groupUnitIds?: string[];
  // When each archived one of them was archived: a cost that began after
  // that day was not split over it (see ComputeInput.unitArchivedOn).
  unitArchivedOn?: Record<string, string>;
  provisionsExpected?: number;
  // Tenant mode: the tenant's amounts per breakdown key and their VAT (the
  // engine's), for the lines that are no cost: a departure's correction
  // (decision 3), and the part of each correction that is provision
  // (PerContractResult.correctionProvisionByKey).
  breakdown?: Record<string, number>;
  breakdownVat?: Record<string, number>;
  correctionProvisionByKey?: Record<string, number>;
  // Tenant mode: the provisions the broker added by hand (part of
  // provisionsExpected).
  extraProvisions?: IExtraProvision[];
  // Owner mode: what departure settlements already settled (the engine's
  // alreadySettled); the statement keeps the lines of the owner's units.
  alreadySettled?: IAlreadySettledLine[];
  landlordTotal?: number;
  // The office of the settled property: the statement's sender. Absent for
  // a property without an office.
  office?: IStatementOffice;
  // The addressee's postal address on file: of the lease's tenants (see
  // leasePostalAddress), or of the owner. A tenant's statement without one
  // goes to the leased unit.
  recipientAddress?: IPostalAddress;
  // Recipient's language; falls back to the broker's own language.
  locale?: string;
  // Owner mode: one owner's part as the engine settled it (a line per cost).
  // The statement then prints exactly those lines instead of re-deriving the
  // owner part — and only the part of that owner's own units.
  ownerResult?: OwnerResult;
  ownerName?: string;
  // Tenant mode: the engine's VAT per cost for this tenant (see
  // PerContractResult.vatByInvoice), so every line prints exactly the VAT the
  // summary and the payment request bill. Absent for older snapshots.
  vatByInvoice?: Record<string, number>;
  // Tenant mode: the departure settlement that settled this lease earlier
  // (see ISettledDeparture), and the engine's share per cost for this tenant
  // (PerContractResult.costByInvoice). With it, every line prints exactly the
  // share the summary and the payment bill.
  departure?: Pick<ISettledDeparture, "until" | "costByInvoice">;
  costByInvoice?: Record<string, number>;
  // The statement of a departure settlement itself: its tenant's amount from
  // the metering company stands in for the readings and is spread over the
  // category's costs as the engine did (see departureCompanyAmountsOfCost).
  isDeparture?: boolean;
  // A building settlement (2026-10-16): the building's costs and a unit's own
  // costs print in sections, an owner's per own unit, and the lease's
  // provision per slice (provisionBySlice, provisionMonthsBySlice).
  isBuildingSettlement?: boolean;
  provisionBySlice?: PerContractResult["provisionBySlice"];
  provisionMonthsBySlice?: PerContractResult["provisionMonthsBySlice"];
  // Tenant mode: the tenant's balance per kind (PerContractResult.kinds).
  kinds?: ISettlementKind[];
  // Tenant mode: the kinds not settled with the tenant because the lease
  // prices them as a forfait (PerContractResult.forfaitKinds): their costs
  // are left out. The statement never names them (Lewis, 2026-10-20).
  fixedFeeKinds?: TFixedFeeKindKey[];
  // Tenant mode: the days of the period a kind was a forfait while its other
  // days are settled (PerContractResult.forfaitIntervalsBySlice, N71): the
  // statement counts none of them. A forfait is never named on the letter
  // (Lewis, 2026-10-20), it only never appears in the amounts.
  forfaitIntervalsBySlice?: PerContractResult["forfaitIntervalsBySlice"];
}

// The tenant's kinds as the statement prints them, with every name the PDF
// cannot pick by itself frozen in the recipient's language. Undefined when
// there are fewer than two, or when they do not add up to the statement's
// own costs, VAT and balance to the cent: the statement then prints its one
// balance as before.
export const statementKindsOf = (params: {
  kinds?: ISettlementKind[];
  grossExcl: number;
  grossVat: number;
  provisionsExpected: number;
  balance: number;
  locale?: string;
}): StatementKind[] | undefined => {
  const kinds = shownKinds({
    kinds: params.kinds,
    cost: params.grossExcl,
    costVat: params.grossVat,
    provision: params.provisionsExpected,
  });
  if (!kinds) return undefined;
  const balanceCents = kinds.reduce(
    (sum, kind) => sum + toCents(kind.balance),
    0,
  );
  if (balanceCents !== toCents(params.balance)) return undefined;
  const locale = toLocale(params.locale);
  const text = (key: string) => getLocalizedText(key, {}, 0, locale);
  return kinds.map(kind => ({
    key: kind.key,
    ...(kind.key !== "common" && kind.key !== "private"
      ? {
          title:
            kind.title?.trim() ||
            text("settlement.provisions.line.additional_fallback"),
        }
      : {}),
    costExcl: kind.cost,
    costVat: kind.costVat,
    provisionLines: kind.provisions.map(provision => {
      if (provision.source === "added") {
        return {
          type: "added" as const,
          title:
            provision.title?.trim() ||
            text("settlement.provisions.extra_fallback_title"),
          amount: provision.amount,
        };
      }
      const months =
        provision.months != null ? { months: provision.months } : {};
      if (provision.lineKind === "additional") {
        return {
          type: "line" as const,
          title:
            provision.title?.trim() ||
            text("settlement.provisions.line.additional_fallback"),
          amount: provision.amount,
          ...months,
        };
      }
      return {
        type:
          provision.slice === "common"
            ? ("common" as const)
            : ("private" as const),
        amount: provision.amount,
        ...months,
      };
    }),
    balance: kind.balance,
  }));
};

// The provision a correction of a lease end pays back or gives back
// (decision 3), named by its days in the recipient's language: "Correctie
// provisie 01/07/2026 – 31/07/2026".
const correctionProvisionLabel = (
  period: { from: string; to: string },
  locale: string | undefined,
): string => {
  const dayText = (iso: string) => {
    const [year, month, day] = iso.slice(0, 10).split("-");
    return `${day}/${month}/${year}`;
  };
  return getLocalizedText(
    "settlement.departure_correction.provision_label",
    { from: dayText(period.from), to: dayText(period.to) },
    0,
    toLocale(locale),
  );
};

const laterDay = (a: string, b: string) => (a > b ? a : b);
const earlierDay = (a: string, b: string) => (a < b ? a : b);

const catLabel = (key: string): string => {
  const stripped = key.endsWith(" (privé)")
    ? key.slice(0, -" (privé)".length)
    : key;
  return CATEGORY_LABELS_NL[stripped as Category] ?? stripped;
};

const PRIVATE_SUFFIX = " (privé)";

// Between a unit's id and a breakdown key in the key of an owner's group of
// one own unit (a building settlement).
const UNIT_GROUP_SEPARATOR = "|";

// The breakdown key of a statement group's key.
const labelOfGroup = (groupKey: string): string =>
  groupKey.includes(UNIT_GROUP_SEPARATOR)
    ? groupKey.slice(groupKey.indexOf(UNIT_GROUP_SEPARATOR) + 1)
    : groupKey;

const joinUnique = (values: (string | undefined)[]): string =>
  [...new Set(values.filter((v): v is string => !!v))].join(", ");

// A line's departure differences, one per tenant, so the lines merged per
// category and VAT rate print each tenant once.
const mergeDepartureDiffs = (
  diffs: { tenantName: string; amount: number }[],
): { tenantName: string; amount: number }[] => {
  const byTenant = new Map<string, number>();
  for (const diff of diffs) {
    byTenant.set(
      diff.tenantName,
      roundCents((byTenant.get(diff.tenantName) ?? 0) + diff.amount),
    );
  }
  return [...byTenant].map(([tenantName, amount]) => ({ tenantName, amount }));
};

// One statement line for several invoices of the same category and VAT rate:
// the invoices together, the party's amounts and VAT summed (so totals and
// VAT stay exactly what the engine charged).
const mergeLines = (lines: StatementLineDetail[]): StatementLineDetail => {
  const sum = (pick: (line: StatementLineDetail) => number | undefined) =>
    roundCents(lines.reduce((s, line) => s + (pick(line) ?? 0), 0));
  const ownerPcts = new Set(lines.map(line => line.ownerFixedPct));
  const merged: StatementLineDetail = {
    date: lines.reduce((max, l) => (l.date > max ? l.date : max), ""),
    description: joinUnique(lines.map(line => line.description)),
    supplier: joinUnique(lines.map(line => line.supplier)) || undefined,
    periodFrom: lines.reduce(
      (min, l) => (l.periodFrom < min ? l.periodFrom : min),
      lines[0].periodFrom,
    ),
    periodTo: lines.reduce(
      (max, l) => (l.periodTo > max ? l.periodTo : max),
      lines[0].periodTo,
    ),
    temporalLabel: "—",
    invoiceExcl: sum(line => line.invoiceExcl),
    recognizedExcl: sum(line => line.recognizedExcl),
    vatRate: lines[0].vatRate,
    vatValue: sum(line => line.vatValue),
  };
  if (lines.some(line => line.ownerFixedExcl != null)) {
    merged.ownerFixedPct =
      ownerPcts.size === 1 ? lines[0].ownerFixedPct : undefined;
    merged.ownerFixedExcl = sum(line => line.ownerFixedExcl);
    merged.ownerVacancyExcl = sum(line => line.ownerVacancyExcl);
  }
  const departureDiffs = mergeDepartureDiffs(
    lines.flatMap(line => line.ownerDepartureDiffs ?? []),
  );
  if (departureDiffs.length > 0) merged.ownerDepartureDiffs = departureDiffs;
  return merged;
};

const mergeByVatRate = (
  lines: StatementLineDetail[],
): StatementLineDetail[] => {
  const byRate = new Map<number, StatementLineDetail[]>();
  for (const line of lines) {
    byRate.set(line.vatRate, [...(byRate.get(line.vatRate) ?? []), line]);
  }
  return [...byRate.values()].map(group =>
    group.length === 1 ? group[0] : mergeLines(group),
  );
};

// What the statement concerns ("Betreft"), frozen in the recipient's
// language: the settled property's full address, the leased unit's for a
// tenant. An owner of part of a group is named by their own units: one
// unit's full address, or for two or more the building's address and their
// count (Lewis, 2026-10-19), "Scenariolaan 1, 9000 Gent — 4 panden", instead
// of every unit's address in a row.
export const statementBuildingLabel = (params: {
  mode: "tenant" | "owner";
  groupId?: string;
  // Owner mode: the owner's own units (OwnerResult.unitIds).
  ownUnitIds?: string[];
  // The settled property, or the leased unit for a tenant.
  property?: AssignableProperty;
  assignable: AssignableProperty[];
  locale?: string;
}): string => {
  const {
    mode,
    groupId,
    ownUnitIds = [],
    property,
    assignable,
    locale,
  } = params;
  const find = (id: string) =>
    assignable.find(candidate => String(candidate.id) === String(id));
  const labelOf = (id: string) => {
    const found = find(id);
    return found ? unitAddressLabel(found, locale) : id;
  };
  if (mode === "owner" && groupId && ownUnitIds.length > 0) {
    const building = find(groupId);
    if (ownUnitIds.length === 1 || !building) {
      return ownUnitIds.map(labelOf).join(", ");
    }
    return getLocalizedText(
      "settlement.statement.owner_units",
      {
        address: unitAddressLabel(building, locale),
        count: String(ownUnitIds.length),
      },
      0,
      toLocale(locale),
    );
  }
  return property
    ? unitAddressLabel(property, locale)
    : getLocalizedText("costs.field.property", {}, 0, toLocale(locale));
};

// S3 render-key for a statement document. The suffix is a content hash of the
// statement itself: change the data (e.g. a bug fix in the balance math) and
// the key changes with it, so a poll can never surface a stale render made
// from older data — mirrors how mijnhuurprofiel's profile PDF stays fresh.
export const statementVariant = (
  mode: "tenant" | "owner",
  // The contract for a tenant statement; the owner key for an owner
  // statement (one per owner when a group's units have different owners).
  partyId: string | undefined,
  statement: StatementSnapshot,
): string => {
  const safeId = (partyId ?? "").replace(/[^a-z0-9]+/gi, "-");
  const base =
    mode === "owner"
      ? safeId
        ? `owner-${safeId}`
        : "owner"
      : `tenant-${safeId}`;
  const str = JSON.stringify(statement);
  let hash = 5381;
  for (let i = 0; i < str.length; i += 1) {
    // eslint-disable-next-line no-bitwise
    hash = ((hash << 5) + hash + str.charCodeAt(i)) | 0;
  }
  // eslint-disable-next-line no-bitwise
  return `${base}-${(hash >>> 0).toString(36)}`;
};

export const buildStatementSnapshot = (
  params: BuildParams,
): StatementSnapshot => {
  const {
    mode,
    settlement,
    name,
    contract,
    contracts,
    keys,
    selectedKeyId,
    distribution,
    assignable,
    provisionsExpected = 0,
    extraProvisions = [],
    alreadySettled = [],
    landlordTotal = 0,
    office,
    recipientAddress,
    locale = getLocale(),
    ownerResult,
    ownerName,
    vatByInvoice,
    departure,
    costByInvoice,
    isDeparture = false,
    isBuildingSettlement = false,
    fixedFeeKinds = [],
  } = params;
  // Each cost as the engine billed it: an ownership cost an older record
  // still leaves to the tenant is the owner's (see withBilledTarget).
  const costs = params.costs.map(withBilledTarget);

  // For a tenant statement on a group we address the specific unit (with its
  // bus number), not the building. Unit scope + owner statements use the
  // settled property itself.
  const labelPropertyId =
    mode === "tenant" && contract?.unitId
      ? contract.unitId
      : settlement.unitId ?? settlement.groupId ?? "";
  const property = assignable.find(p => p.id === labelPropertyId);
  // A property's full address in the recipient's language, its box spelled
  // out ("Scenariolaan 1 bus 2, 9000 Gent"; formatAddress writes
  // "Scenariolaan 1 2", which reads as house number 12).
  const labelOf = (id: string) => {
    const p = assignable.find(a => String(a.id) === String(id));
    return p ? unitAddressLabel(p, locale) : id;
  };
  const buildingLabel = statementBuildingLabel({
    mode,
    groupId: settlement.groupId,
    ownUnitIds: ownerResult?.unitIds,
    property,
    assignable,
    locale,
  });

  // Every unit of the group — the apportionment denominator spans occupied AND
  // vacant units, so a vacant unit's slice lands on the owner instead of
  // inflating the tenants. Same rule the settlement engine applies. A
  // departure settlement passes them too: without, its statement split a
  // group cost over the departing tenant's unit alone.
  const groupUnitIds =
    params.groupUnitIds ??
    (settlement.groupId
      ? assignable
          .filter(p => p.parentId === settlement.groupId)
          .map(p => String(p.id))
      : []);

  const detailsByCat: Record<string, StatementLineDetail[]> = {};
  // What the broker specified for the "Other" costs of each category group,
  // so the cover names "Andere — Tuinonderhoud" instead of just "Andere".
  const specsByCat: Record<string, string[]> = {};
  const addSpec = (label: string, inv: Invoice) => {
    const spec = categorySpecOf(inv);
    const specs = (specsByCat[label] ??= []);
    if (spec && !specs.includes(spec)) specs.push(spec);
  };
  const lineDescription = (inv: Invoice) =>
    inv.description?.trim() || categorySpecOf(inv) || inv.supplier || "—";
  // A building settlement keeps an owner's units apart: the lines of each
  // own unit form groups of their own (keyed "<unitId>|<label>").
  const unitOfGroup: Record<string, string> = {};
  const groupKeyOf = (label: string, unitId?: string) => {
    if (!isBuildingSettlement || !unitId) return label;
    const key = `${unitId}${UNIT_GROUP_SEPARATOR}${label}`;
    unitOfGroup[key] = unitId;
    return key;
  };
  if (mode === "owner" && ownerResult) {
    const costById = new Map(costs.map(c => [c.id, c]));
    for (const line of ownerResult.lines) {
      const inv = costById.get(line.invoiceId);
      if (!inv) continue;
      const groupKey = groupKeyOf(line.label, line.unitId);
      addSpec(groupKey, inv);
      (detailsByCat[groupKey] ??= []).push({
        date: inv.date,
        description: lineDescription(inv),
        supplier: inv.supplier,
        periodFrom: inv.periodFrom,
        periodTo: inv.periodTo,
        temporalLabel: "—",
        invoiceExcl: roundCents(inv.amountExclVat),
        recognizedExcl: line.amount,
        vatRate: vatPercent(inv.vatPct ?? 0),
        vatValue: line.vat,
        ownerFixedPct: line.ownerPct,
        ownerFixedExcl: line.base,
        ownerVacancyExcl: line.vacancy,
        ...(line.departureDiffs?.length
          ? {
              ownerDepartureDiffs: mergeDepartureDiffs(
                line.departureDiffs.map(diff => ({
                  tenantName: diff.tenantName,
                  amount: diff.amount,
                })),
              ),
            }
          : {}),
      });
    }
  } else {
    // What a metering company's amounts cover: the category's group costs
    // together, the same total the engine splits them over.
    const commonTotalByCat: Record<string, number> = {};
    const commonCostsByCat: Record<string, Invoice[]> = {};
    for (const c of costs) {
      if (c.scope !== "common") continue;
      commonTotalByCat[c.category] =
        (commonTotalByCat[c.category] ?? 0) + settlementAmount(c);
      (commonCostsByCat[c.category] ??= []).push(c);
    }
    // The days the lease's kind of a cost was a forfait (N71): the engine
    // charges none of them, so the statement counts none of them either.
    const fixedFeeDaysBySlice =
      mode === "tenant" && contract
        ? fixedFeeDaysOfLease(contract, params.forfaitIntervalsBySlice)
        : {};
    // The lease's days in `range` its kind of `inv` was a forfait.
    const fixedFeeDaysIn = (
      inv: Invoice,
      range: { from: string; to: string },
    ): number => {
      const kind = costKindOf(inv);
      const days = isFixedFeeKindKey(kind)
        ? fixedFeeDaysBySlice[sliceOfFixedFeeKind(kind)]
        : undefined;
      if (!contract || !days) return 0;
      const from = laterDay(contract.startDate.slice(0, 10), range.from);
      const to = earlierDay(contract.endDate.slice(0, 10), range.to);
      return from > to ? 0 : daysWithin(days, { from, to });
    };
    for (const inv of costs) {
      // A kind the lease prices as a forfait is not settled with this
      // tenant: the engine gave their share of its costs to the owner.
      if (
        mode === "tenant" &&
        fixedFeeKinds.some(kind => kind === costKindOf(inv))
      ) {
        continue;
      }
      const period = { from: inv.periodFrom, to: inv.periodTo };
      let splitLabel: string | undefined;
      const tenantSharePct =
        inv.target === "owner"
          ? 0
          : inv.target === "shared"
            ? inv.tenantSharePct ?? 0
            : 100;
      let recognized = 0;
      let temporalLabel = "—";
      // Owner mode: the owner's share split into its fixed part and the part
      // caused by vacancy, so the owner's statement shows how it was computed.
      let ownerFixedExcl = 0;
      let ownerVacancyExcl = 0;

      if (inv.scope === "private") {
        // Privative cost: belongs to one unit, split over that unit's tenants by
        // occupied days; vacant days fall on the owner.
        const unitContracts = contracts.filter(c => c.unitId === inv.unitId);
        const proration = prorateByOccupancy(
          inv.amountExclVat,
          tenantSharePct,
          unitContracts,
          period,
        );
        if (mode === "tenant" && contract) {
          const share = proration.shares.find(
            s => s.contractId === contract.id,
          );
          if (share) {
            recognized = share.amount;
            temporalLabel = `${Math.max(
              0,
              share.days - fixedFeeDaysIn(inv, period),
            )} / ${proration.periodDays}`;
          }
        } else if (mode === "owner") {
          recognized = proration.ownerTotal;
          ownerFixedExcl = proration.ownerBase;
          ownerVacancyExcl = proration.vacancyAmount;
          temporalLabel = `${proration.periodDays} / ${proration.periodDays}`;
        }
      } else {
        // Common (group) cost: it lives on the GROUP property, so it can't be
        // prorated over "its own unit" — the apportionment key spreads it across
        // the group's units first. Delegating to the engine keeps the PDF's
        // numbers identical to the Samenvatting the user approved.
        const key = keys.find(k => k.id === selectedKeyId[inv.category]);
        const cat = inv.category;
        // Snapshots frozen before the distribution was persisted carry no method
        // at all. Those numbers were produced with the key, so keep that for
        // them — resolving the category default instead would re-render an old
        // statement with a different split than its own frozen perContract.
        const method = distribution?.distributionMethod
          ? resolveDistributionMethod(cat, distribution.distributionMethod, {
              isDeparture,
            })
          : "key";
        // The units that existed during this cost's own period, and their
        // leases, as the engine split it.
        const existedForCost = (unitId: string) =>
          existedSince({
            archivedOn: params.unitArchivedOn?.[unitId],
            from: inv.periodFrom,
          });
        const costUnitIds = groupUnitIds.filter(existedForCost);
        const costContracts = contracts.filter(c => existedForCost(c.unitId));
        const companyAmounts = distribution?.companyAmounts?.[cat] ?? {};
        const isDepartureCompany = isDeparture && method === "company";
        const res = allocate({
          contracts: costContracts,
          invoices: [inv],
          method,
          keyWeights: key?.weights ?? {},
          // Same fallback as the engine: no key chosen → equal split.
          isEqualKey: key ? !!key.isEqual : true,
          consumption: distribution?.consumption?.[cat] ?? {},
          buildingTotal: distribution?.buildingTotal?.[cat] ?? 0,
          excessMode: distribution?.excessMode?.[cat] ?? "landlord",
          companyAmounts: isDepartureCompany
            ? departureCompanyAmountsOfCost({
                invoice: inv,
                categoryCosts: commonCostsByCat[cat] ?? [],
                companyAmounts,
              })
            : companyAmounts,
          companyTotal: isDepartureCompany
            ? settlementAmount(inv)
            : commonTotalByCat[cat],
          period,
          unitIds: costUnitIds,
        });
        if (mode === "tenant" && contract) {
          recognized = res.perContract[contract.id] ?? 0;
          const showKeyRatio = method === "key";
          // Labels describe the addressed unit: its key share and the days this
          // contract occupied the cost period.
          const keyWeightOf = (unitId: string) =>
            key?.isEqual ? 1 : key?.weights[unitId] ?? 0;
          const allUnitIds = [
            ...new Set([...costUnitIds, ...costContracts.map(c => c.unitId)]),
          ];
          // Same rule as the engine: no key, or one that covers none of the
          // group's units, splits equally.
          const splitsEqually = !allUnitIds.some(u => keyWeightOf(u) > 0);
          const weightOf = (unitId: string) =>
            splitsEqually ? 1 : keyWeightOf(unitId);
          const totalWeight = allUnitIds.reduce((s, u) => s + weightOf(u), 0);
          if (showKeyRatio && totalWeight > 0) {
            // Belgian notation ("80,5 / 180,5"), like every amount on the
            // statement.
            splitLabel = `${formatWeight(
              weightOf(contract.unitId),
            )} / ${formatWeight(totalWeight)}`;
          }
          const unitProration = prorateByOccupancy(
            inv.amountExclVat,
            100,
            contracts.filter(c => c.unitId === contract.unitId),
            period,
          );
          const share = unitProration.shares.find(
            s => s.contractId === contract.id,
          );
          // A reading or company amount per tenant is that tenant's own; no
          // share of occupied days went into it.
          const perOccupant =
            (method === "consumption" &&
              isPerOccupant(distribution?.consumption?.[cat] ?? {})) ||
            (method === "company" &&
              isPerOccupant(distribution?.companyAmounts?.[cat] ?? {}));
          if (share && !perOccupant) {
            temporalLabel = `${Math.max(
              0,
              share.days - fixedFeeDaysIn(inv, period),
            )} / ${unitProration.periodDays}`;
          }
        } else if (mode === "owner") {
          // allocate() distributes the tenant slice; the structural owner share
          // of shared/owner-target common costs comes on top — same correction
          // the engine applies in computeResult, as the exact remainder of
          // the cent-exact tenant slice.
          const structuralOwner = fromCents(
            toCents(inv.amountExclVat) - toCents(settlementAmount(inv)),
          );
          recognized = res.landlord + structuralOwner;
          ownerFixedExcl = structuralOwner;
          ownerVacancyExcl = res.landlord;
        }
      }
      // The share the engine charged this tenant, when it is known: the
      // summary, the balance and the payment bill exactly that. Re-deriving
      // it here missed what the engine leaves out, e.g. the days the lease's
      // kind was a forfait (N71), so the letter billed a whole year.
      if (mode === "tenant" && contract && costByInvoice) {
        recognized = costByInvoice[inv.id] ?? 0;
      }
      // A cost this lease's departure settlement held: the departure charged
      // its days up to then, so this statement prints only what the engine
      // charged on top, the days after it when the lease runs longer now.
      // Re-deriving the share charged the held part a second time on paper.
      if (
        mode === "tenant" &&
        contract &&
        departure?.costByInvoice[inv.id] != null
      ) {
        recognized = costByInvoice?.[inv.id] ?? 0;
        if (temporalLabel !== "—") {
          const daysAfter =
            overlapDays(
              contract.startDate,
              contract.endDate,
              period.from,
              period.to,
            ) -
            overlapDays(
              contract.startDate,
              departure.until,
              period.from,
              period.to,
            );
          const fixedFeeDaysAfter =
            fixedFeeDaysIn(inv, period) -
            fixedFeeDaysIn(inv, {
              from: period.from,
              to: earlierDay(departure.until.slice(0, 10), period.to),
            });
          temporalLabel = `${Math.max(
            0,
            daysAfter - fixedFeeDaysAfter,
          )} / ${daysInPeriod(period.from, period.to)}`;
        }
      }
      if (Math.abs(recognized) < 0.005) continue;
      // vatPct can be an ENoVatType string ("no VAT" reasons) — Number() on
      // those yields NaN and poisons every total downstream. vatPercent maps
      // them to an effective 0%.
      const vatRate = vatPercent(inv.vatPct ?? 0);
      // The engine's own VAT for this tenant and cost (its cent-exact part of
      // the invoice's VAT); older snapshots round the share's VAT half-up.
      const vatValue =
        (mode === "tenant" ? vatByInvoice?.[inv.id] : undefined) ??
        vatOfAmount({ amountExclVat: recognized, vatPct: vatRate });
      const label =
        inv.scope === "private" ? `${inv.category} (privé)` : inv.category;
      addSpec(label, inv);
      (detailsByCat[label] ??= []).push({
        date: inv.date,
        description: lineDescription(inv),
        supplier: inv.supplier,
        periodFrom: inv.periodFrom,
        periodTo: inv.periodTo,
        splitLabel,
        temporalLabel,
        invoiceExcl: roundCents(inv.amountExclVat),
        recognizedExcl: roundCents(recognized),
        vatRate,
        vatValue,
        ...(mode === "owner"
          ? {
              ownerFixedPct: 100 - tenantSharePct,
              ownerFixedExcl: roundCents(ownerFixedExcl),
              ownerVacancyExcl: roundCents(ownerVacancyExcl),
            }
          : {}),
      });
    }
  }

  // A metering company's amounts and per-tenant meter readings are entered for
  // the category as a whole; the engine only spreads them over its invoices
  // by invoice size. Per invoice such a share explains nothing, so those
  // categories print one line (per VAT rate): the category total next to the
  // party's own amount.
  for (const label of Object.keys(detailsByCat)) {
    if (label.endsWith(PRIVATE_SUFFIX) || !distribution?.distributionMethod) {
      continue;
    }
    const cat = labelOfGroup(label) as Category;
    const method = resolveDistributionMethod(
      cat,
      distribution.distributionMethod,
      { isDeparture },
    );
    const billedPerCategory =
      method === "company" ||
      (method === "consumption" &&
        isPerOccupant(distribution.consumption?.[cat] ?? {}));
    if (billedPerCategory) {
      detailsByCat[label] = mergeByVatRate(detailsByCat[label]);
    }
  }

  // A departure's correction (decision 3): a line of its own, no cost,
  // named by its days in the recipient's language. Its amount may hold
  // provision paid back or given back, which carries no VAT: the line holds
  // the costs only, so its VAT rate is theirs, and the provision prints as
  // a provision of its own (a tenant's among their provisions, an owner's,
  // who carries it, as a line of the correction at 0 %).
  const corrections =
    mode === "owner"
      ? (ownerResult?.lines ?? [])
          .filter(line => isDepartureCorrectionKey(line.invoiceId))
          .map(line => ({
            key: line.invoiceId,
            amount: line.amount,
            vat: line.vat,
            provision: line.provision ?? 0,
            unitId: line.unitId,
          }))
      : Object.entries(params.breakdown ?? {})
          .filter(
            ([key, amount]) =>
              isDepartureCorrectionKey(key) && Math.abs(amount) >= 0.005,
          )
          .map(([key, amount]) => ({
            key,
            amount,
            vat: params.breakdownVat?.[key] ?? 0,
            provision: params.correctionProvisionByKey?.[key] ?? 0,
            unitId: undefined as string | undefined,
          }));
  // A tenant's provision of each correction, as the deductions print it.
  const correctionProvisions: {
    kind: TFixedFeeKindKey;
    title: string;
    amount: number;
  }[] = [];
  for (const correction of corrections) {
    const period = parseDepartureCorrectionKey(correction.key);
    if (!period) continue;
    const label =
      departureCorrectionLabel(correction.key, toLocale(locale)) ??
      correction.key;
    const provision = roundCents(correction.provision);
    const costExcl = fromCents(toCents(correction.amount) - toCents(provision));
    const lineOf = (params: {
      description: string;
      amount: number;
      vat: number;
    }): StatementLineDetail => ({
      date: period.to,
      description: params.description,
      periodFrom: period.from,
      periodTo: period.to,
      temporalLabel: "—",
      invoiceExcl: params.amount,
      recognizedExcl: params.amount,
      // Never "-0 %" for a credit without VAT.
      vatRate:
        Math.abs(params.amount) >= 0.005
          ? Math.round((params.vat / params.amount) * 100) || 0
          : 0,
      vatValue: roundCents(params.vat),
    });
    const lines: StatementLineDetail[] = [
      ...(Math.abs(costExcl) >= 0.005 || Math.abs(correction.vat) >= 0.005
        ? [
            lineOf({
              description: label,
              amount: costExcl,
              vat: correction.vat,
            }),
          ]
        : []),
    ];
    if (Math.abs(provision) >= 0.005) {
      const title = correctionProvisionLabel(period, locale);
      if (mode === "owner") {
        lines.push(lineOf({ description: title, amount: provision, vat: 0 }));
      } else {
        correctionProvisions.push({
          kind: correction.key.endsWith(PRIVATE_SUFFIX) ? "private" : "common",
          title,
          // A deduction: provision the tenant pays back comes off as a
          // negative one.
          amount: -provision,
        });
      }
    }
    if (lines.length > 0) {
      (detailsByCat[groupKeyOf(correction.key, correction.unitId)] ??= []).push(
        ...lines,
      );
    }
  }

  // A unit's label on this statement, in the recipient's language: its full
  // address with its box spelled out ("Scenariolaan 1 bus 2, 9000 Gent"), as
  // the screens name it (unitAddressLabel).
  const unitOf = (unitId: string) =>
    assignable.find(a => String(a.id) === String(unitId));
  const unitLabelOf = (unitId: string) => {
    const p = unitOf(unitId);
    return p ? unitAddressLabel(p, locale) : labelOf(unitId);
  };
  // What the units' sections are ordered by: their box ("bus 2" before
  // "bus 10").
  const unitSortLabelOf = (unitId: string) => {
    const p = unitOf(unitId);
    return p ? unitBoxLabel(p, locale) : labelOf(unitId);
  };
  // The tenant's own unit, on a building settlement.
  const tenantUnitId =
    mode === "tenant" && contract?.unitId ? String(contract.unitId) : undefined;
  // The unit whose own costs a category holds; undefined for the building's.
  const unitIdOfGroup = (groupKey: string): string | undefined =>
    labelOfGroup(groupKey).endsWith(PRIVATE_SUFFIX)
      ? unitOfGroup[groupKey] ?? tenantUnitId
      : undefined;
  const sectionOf = (
    groupKey: string,
  ): Pick<StatementLineGroup, "section" | "unitLabel"> => {
    const label = labelOfGroup(groupKey);
    if (!label.endsWith(PRIVATE_SUFFIX)) return { section: "building" };
    const unitId = unitIdOfGroup(groupKey);
    return {
      section: "unit",
      ...(unitId ? { unitLabel: unitLabelOf(unitId) } : {}),
    };
  };
  const sortLabelOfGroup = (groupKey: string): string => {
    const unitId = unitIdOfGroup(groupKey);
    return unitId ? unitSortLabelOf(unitId) : "";
  };
  // Sections only when a unit's own costs are on the statement: one that
  // holds the building's costs alone reads as it always did.
  const isSectioned =
    isBuildingSettlement &&
    Object.keys(detailsByCat).some(key =>
      labelOfGroup(key).endsWith(PRIVATE_SUFFIX),
    );
  const compareGroups = (a: string, b: string): number => {
    if (isSectioned) {
      const sa = sectionOf(a);
      const sb = sectionOf(b);
      if (sa.section !== sb.section) return sa.section === "building" ? -1 : 1;
      const byUnit =
        compareUnitLabels(sortLabelOfGroup(a), sortLabelOfGroup(b)) ||
        compareUnitLabels(sa.unitLabel ?? "", sb.unitLabel ?? "");
      if (byUnit !== 0) return byUnit;
    }
    return compareBreakdownKeys(labelOfGroup(a), labelOfGroup(b));
  };

  // A key's name as the screens show it: one stored in capitals in sentence
  // case (keyDisplayName).
  const keyNameOf = (keyId?: string): string | undefined => {
    const name = keyId ? keys.find(k => k.id === keyId)?.name : undefined;
    return name ? keyDisplayName(name) : undefined;
  };

  // Same category order as the summary cards on screen.
  const categoryGroups: StatementLineGroup[] = Object.keys(detailsByCat)
    .sort(compareGroups)
    .map(groupKey => {
      const key = labelOfGroup(groupKey);
      const details = detailsByCat[groupKey];
      const subtotalExcl = roundCents(
        details.reduce((s, d) => s + d.recognizedExcl, 0),
      );
      const subtotalVat = roundCents(
        details.reduce((s, d) => s + d.vatValue, 0),
      );
      const isPrivate = key.endsWith(" (privé)");
      const baseCategory = (
        isPrivate ? key.slice(0, -" (privé)".length) : key
      ) as Category;
      // Only a category split by key names one: a key id can linger in the
      // draft after the method was switched to consumption or company.
      const splitByKey =
        !distribution?.distributionMethod ||
        resolveDistributionMethod(
          baseCategory,
          distribution.distributionMethod,
          { isDeparture },
        ) === "key";
      const apportionmentKeyName = splitByKey
        ? keyNameOf(selectedKeyId[key as Category])
        : undefined;
      // The owner's fixed part of a consumption or metering-company category
      // was split over the units by a key of its own: name it.
      const ownerPart =
        mode === "owner" &&
        !isPrivate &&
        details.some(d => Math.abs(d.ownerFixedExcl ?? 0) >= 0.005)
          ? ownerPartKeyName({
              method: distribution?.distributionMethod
                ? resolveDistributionMethod(
                    baseCategory,
                    distribution.distributionMethod,
                    { isDeparture },
                  )
                : undefined,
              keyId: selectedKeyId[baseCategory],
              keyNameOf,
              equalKeyName: keyNameOf(
                keys.find(k => k.isSystem && k.isEqual)?.id,
              ),
            })
          : undefined;
      const categorySpecs = specsByCat[groupKey] ?? [];
      const correctionLabel = departureCorrectionLabel(key, toLocale(locale));
      return {
        categoryKey: correctionLabel ?? catLabel(key),
        categoryI18nKey: correctionLabel
          ? undefined
          : CATEGORY_KEYS[baseCategory],
        ...(categorySpecs.length > 0 ? { categorySpecs } : {}),
        isPrivate,
        subtotalExcl,
        subtotalVat,
        details,
        apportionmentKeyName,
        ...(ownerPart ? { ownerPartKeyName: ownerPart } : {}),
        ...(isSectioned ? sectionOf(groupKey) : {}),
      };
    });

  const grossExcl = roundCents(
    categoryGroups.reduce((s, g) => s + g.subtotalExcl, 0),
  );
  const grossVat = roundCents(
    categoryGroups.reduce((s, g) => s + g.subtotalVat, 0),
  );

  // VAT breakdown by rate.
  const vatMap = new Map<number, { excl: number; vat: number }>();
  for (const g of categoryGroups) {
    for (const d of g.details) {
      const b = vatMap.get(d.vatRate) ?? { excl: 0, vat: 0 };
      b.excl += d.recognizedExcl;
      b.vat += d.vatValue;
      vatMap.set(d.vatRate, b);
    }
  }
  const vatBuckets: StatementVatBucket[] = Array.from(vatMap.entries())
    .sort(([a], [b]) => a - b)
    .map(([rate, b]) => ({
      rate,
      excl: roundCents(b.excl),
      vat: roundCents(b.vat),
    }));

  // The tenant statement's "totaalexcl" is the recognised excl-btw amount for
  // the tenant — provisions and balance are only settled after VAT (see PDF).
  const totalExcl =
    mode === "tenant" || ownerResult ? grossExcl : landlordTotal || grossExcl;
  // Balance = what the tenant owes or gets back, after VAT:
  // (excl + VAT) - expected provisions (monthly amount x duration). A settlement
  // weighs the real cost against what the tenant was supposed to pay, not
  // against what was actually collected. The previous calculation dropped VAT,
  // which made the balance come out too low.
  // The owner owes their share incl. VAT too. This used the engine's
  // landlordTotal — an excl-VAT number — so the owner statement printed
  // "Totaal incl. btw" and then a payable amount WITHOUT that VAT right
  // under it. The owner detail rows already carry their own VAT; sum those.
  // The provisions the statement deducts: the expected ones, and each
  // correction's provision (a provision paid back deducts negatively).
  const correctionProvisionTotal = fromCents(
    correctionProvisions.reduce((sum, line) => sum + toCents(line.amount), 0),
  );
  const deductedProvisions = fromCents(
    toCents(provisionsExpected) + toCents(correctionProvisionTotal),
  );
  const tenantBalance =
    mode === "tenant"
      ? roundCents(grossExcl + grossVat - deductedProvisions)
      : roundCents(grossExcl + grossVat);

  const kind: TSettlementKind = isDeparture ? "departure" : "regular";

  // The lease's provision per slice on a building settlement, when its
  // parts make exactly the lease's provision (provisionSliceLines).
  const provisionLines =
    mode === "tenant" && isBuildingSettlement
      ? provisionSliceLines({
          provision: provisionsExpected,
          extraProvisions,
          provisionBySlice: params.provisionBySlice,
          provisionMonthsBySlice: params.provisionMonthsBySlice,
        })
      : undefined;

  // The tenant's balance per kind, when it adds up to this statement. The
  // engine counts a correction's provision among the kind's costs; the
  // statement deducts it with the kind's provisions instead.
  const kinds =
    mode === "tenant"
      ? statementKindsOf({
          kinds: params.kinds,
          grossExcl: fromCents(
            toCents(grossExcl) - toCents(correctionProvisionTotal),
          ),
          grossVat,
          provisionsExpected,
          balance: tenantBalance,
          locale,
        })?.map(statementKind => {
          const own = correctionProvisions.filter(
            line => line.kind === statementKind.key,
          );
          if (own.length === 0) return statementKind;
          return {
            ...statementKind,
            costExcl: fromCents(
              toCents(statementKind.costExcl) +
                own.reduce((sum, line) => sum + toCents(line.amount), 0),
            ),
            provisionLines: [
              ...statementKind.provisionLines,
              ...own.map(line => ({
                type: "added" as const,
                title: line.title,
                amount: line.amount,
              })),
            ],
          };
        })
      : undefined;

  // The addressee: the lease's tenants at their own address, else at the
  // leased unit; or the owner, at their address when it is known.
  const recipient: StatementParty =
    mode === "tenant" && contract
      ? {
          name: contract.tenantName,
          lines: recipientAddress
            ? postalAddressLines(recipientAddress, locale)
            : unitPostalLines(
                assignable.find(p => String(p.id) === String(contract.unitId)),
                locale,
              ),
        }
      : {
          name:
            ownerName ||
            getLocalizedText("costs.split.owner", {}, 0, toLocale(locale)),
          lines: postalAddressLines(recipientAddress, locale),
        };

  return {
    mode,
    locale,
    title:
      name?.trim() ||
      settlementStoredName(settlement) ||
      getSettlementTitle({
        kind,
        from: settlement.periodFrom,
        to: settlement.periodTo,
        withLeaseEnd: true,
        locale,
      }),
    kind,
    // rentio-api-next numbers a confirmed settlement's statement, or marks it
    // as a preview, when it renders it.
    reference: "",
    docDate: todayIsoDay(),
    buildingLabel,
    periodFrom: settlement.periodFrom,
    periodTo: settlement.periodTo,
    ...(office
      ? {
          sender: {
            name: office.name,
            lines: postalAddressLines(office.address, locale),
          },
        }
      : {}),
    recipient,
    tenantName: contract?.tenantName,
    ...(mode === "tenant" && contract?.tenantNames
      ? {
          tenantNames: contract.tenantNames,
          parentNames: contract.parentNames ?? [],
        }
      : {}),
    contractStart: contract?.startDate,
    contractEnd: contract?.endDate,
    categoryGroups,
    grossExcl,
    grossVat,
    provisionsExpected: mode === "tenant" ? deductedProvisions : undefined,
    ...(() => {
      if (mode !== "owner") return {};
      // The owner of part of a group sees their own units only.
      const ownUnits = ownerResult ? new Set(ownerResult.unitIds) : undefined;
      const lines = alreadySettled
        .filter(line => !ownUnits || ownUnits.has(String(line.unitId)))
        .map(line => {
          const baseCategory = (
            line.label.endsWith(PRIVATE_SUFFIX)
              ? line.label.slice(0, -PRIVATE_SUFFIX.length)
              : line.label
          ) as Category;
          return {
            tenantName: line.tenantName,
            unitLabel: labelOf(line.unitId),
            until: line.until,
            categoryKey: catLabel(line.label),
            categoryI18nKey: CATEGORY_KEYS[baseCategory],
            isPrivate: line.label.endsWith(PRIVATE_SUFFIX),
            amount: line.amount,
          };
        });
      return lines.length > 0 ? { alreadySettled: lines } : {};
    })(),
    // The provisions added by hand, then each correction's provision.
    ...(mode === "tenant" &&
    extraProvisions.length + correctionProvisions.length > 0
      ? {
          extraProvisions: [
            ...extraProvisions.map(extra => ({
              title: extraProvisionTitle(extra),
              amount: extra.amount,
            })),
            ...correctionProvisions.map(line => ({
              title: line.title,
              amount: line.amount,
            })),
          ],
        }
      : {}),
    ...(provisionLines ? { provisionLines } : {}),
    ...(kinds ? { kinds } : {}),
    totalExcl,
    totalVat: grossVat,
    balance: tenantBalance,
    vatBuckets,
  };
};
